
import React, { useEffect, useState } from 'react';
import { useApp } from '../AppContext';
import { 
  Plus, ExternalLink, ArrowLeft, Loader2, Store as StoreIcon, 
  X, Save, CheckCircle2, AlertCircle, Trash2, Edit, 
  LogOut, Key, Package, Tag, Terminal, Copy, Upload, Image as ImageIcon, XCircle, PackagePlus, Mail, Lock, UserPlus, Globe, AlertTriangle, Share2, Ticket
} from 'lucide-react';
import { storeService, productService, couponService, generateSlug } from '../lib/storeService';
import { supabase } from '../lib/supabaseClient';
import { Store, Product, Coupon } from '../types';

type AuthMode = 'signin' | 'signup' | 'forgot_password';
type AdminTab = 'products' | 'coupons';

const AdminPage: React.FC = () => {
  const { stores, fetchStores, isLoading: isStoresLoading, user, signOut, error: contextError } = useApp();
  
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('products');
  
  const [products, setProducts] = useState<Product[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  // Estados de Autenticação
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authSuccessMsg, setAuthSuccessMsg] = useState<string | null>(null);

  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  
  const [storeToDelete, setStoreToDelete] = useState<Store | null>(null);
  const [deleteConfirmationName, setDeleteConfirmationName] = useState('');

  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Estados para Upload
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [productFile, setProductFile] = useState<File | null>(null);
  const [productPreview, setProductPreview] = useState<string | null>(null);

  const [storeFormData, setStoreFormData] = useState({ 
    name: '', slug: '', whatsapp_number: '', primary_color: '#4f46e5', logo_url: '', banner_url: ''
  });

  const [productFormData, setProductFormData] = useState({
    name: '', description: '', price: 0, unit: 'un', category: 'Geral', image_url: '', active: true
  });
  
  const [couponFormData, setCouponFormData] = useState<Omit<Coupon, 'id' | 'store_id' | 'created_at'>>({
    code: '', type: 'percent', value: 0, active: true
  });

  useEffect(() => {
    if (user) fetchStores();
  }, [user]);

  useEffect(() => {
    if (selectedStore) {
      if (activeTab === 'products') loadProducts(selectedStore.id);
      else loadCoupons(selectedStore.id);
    }
  }, [selectedStore, activeTab]);

  const handleStoreNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setStoreFormData(prev => ({
      ...prev,
      name,
      slug: prev.slug === generateSlug(prev.name) ? generateSlug(name) : prev.slug
    }));
  };

  const handleShareStore = (slug: string) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const shareUrl = `${baseUrl}#/${slug}`;
    
    if (navigator.share) {
      navigator.share({ title: 'Confira nossa loja!', url: shareUrl }).catch(() => copyToClipboard(shareUrl));
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccessMsg("Link copiado! Envie para seus clientes.");
      setTimeout(() => setSuccessMsg(null), 3000);
    });
  };

  const loadProducts = async (storeId: string) => {
    setIsDataLoading(true);
    setErrorMsg(null);
    try {
      const { data, error } = await productService.fetchProducts(storeId);
      if (error) throw error;
      setProducts(data || []);
    } catch (err: any) {
      setErrorMsg("Erro ao carregar estoque: " + err.message);
    } finally {
      setIsDataLoading(false);
    }
  };

  const loadCoupons = async (storeId: string) => {
    setIsDataLoading(true);
    try {
      const { data, error } = await couponService.fetchCoupons(storeId);
      if (error) throw error;
      setCoupons(data || []);
    } catch (err: any) {
      setErrorMsg("Erro ao carregar cupons");
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsAuthLoading(true);
    try {
      if (authMode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (authMode === 'signup') {
        const { error, data } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        if (data.user && data.session === null) {
          setAuthSuccessMsg("Conta criada! Verifique seu e-mail.");
          setAuthMode('signin');
        }
      }
    } catch (err: any) { setAuthError(err.message); }
    finally { setIsAuthLoading(false); }
  };

  const openStoreModal = (store?: Store) => {
    setErrorMsg(null);
    setLogoFile(null);
    setBannerFile(null);
    if (store) {
      setEditingStore(store);
      setStoreFormData({ name: store.name, slug: store.slug, whatsapp_number: store.whatsapp_number, primary_color: store.primary_color, logo_url: store.logo_url || '', banner_url: store.banner_url || '' });
      setLogoPreview(store.logo_url);
      setBannerPreview(store.banner_url);
    } else {
      setEditingStore(null);
      setStoreFormData({ name: '', slug: '', whatsapp_number: '', primary_color: '#4f46e5', logo_url: '', banner_url: '' });
      setLogoPreview(null);
      setBannerPreview(null);
    }
    setIsStoreModalOpen(true);
  };

  const handleStoreSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let lUrl = storeFormData.logo_url;
      let bUrl = storeFormData.banner_url;
      if (logoFile) {
        const up = await storeService.uploadFile(logoFile, 'media', 'logos');
        if (up) lUrl = up;
      }
      if (bannerFile) {
        const up = await storeService.uploadFile(bannerFile, 'media', 'banners');
        if (up) bUrl = up;
      }
      const res = editingStore 
        ? await storeService.updateStore(editingStore.id, { ...storeFormData, logo_url: lUrl, banner_url: bUrl })
        : await storeService.createStore({ ...storeFormData, logo_url: lUrl, banner_url: bUrl });
      if (res.error) throw res.error;
      setSuccessMsg("Loja salva!");
      setIsStoreModalOpen(false);
      fetchStores();
    } catch (err: any) { setErrorMsg(err.message); }
    finally { setIsSubmitting(false); }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStore) return;
    setIsSubmitting(true);
    try {
      let iUrl = productFormData.image_url;
      if (productFile) {
        const up = await storeService.uploadFile(productFile, 'media', 'products');
        if (up) iUrl = up;
      }
      const finalData = { ...productFormData, image_url: iUrl };
      const res = editingProduct
        ? await productService.updateProduct(editingProduct.id, finalData)
        : await productService.createProduct({ ...finalData, store_id: selectedStore.id });
      if (res.error) throw res.error;
      setSuccessMsg("Produto salvo!");
      setIsProductModalOpen(false);
      loadProducts(selectedStore.id);
    } catch (err: any) { setErrorMsg(err.message); }
    finally { setIsSubmitting(false); }
  };

  const handleCouponSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStore) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('coupons').insert([{
        ...couponFormData,
        store_id: selectedStore.id,
        code: couponFormData.code.toUpperCase().trim()
      }]);
      if (error) throw error;
      setSuccessMsg("Cupom criado!");
      setIsCouponModalOpen(false);
      setCouponFormData({ code: '', type: 'percent', value: 0, active: true });
      loadCoupons(selectedStore.id);
    } catch (err: any) { setErrorMsg(err.message); }
    finally { setIsSubmitting(false); }
  };

  const confirmDeleteAction = async () => {
    if (!storeToDelete) return;
    setIsSubmitting(true);
    try {
      const { error } = await storeService.deleteStore(storeToDelete.id);
      if (error) throw error;
      setSuccessMsg("Loja excluída.");
      setStoreToDelete(null);
      fetchStores();
    } catch (err: any) { setErrorMsg(err.message); }
    finally { setIsSubmitting(false); }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'banner' | 'product') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === 'logo') { setLogoFile(file); setLogoPreview(url); }
    else if (type === 'banner') { setBannerFile(file); setBannerPreview(url); }
    else { setProductFile(file); setProductPreview(url); }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-[2.5rem] shadow-2xl border border-indigo-100 overflow-hidden">
            <div className="bg-indigo-600 p-10 text-center">
              <div className="inline-flex items-center justify-center p-4 bg-white/20 backdrop-blur-md rounded-3xl text-white mb-6">
                {authMode === 'signin' ? <Key size={32} /> : <UserPlus size={32} />}
              </div>
              <h1 className="text-3xl font-black text-white">
                {authMode === 'signin' ? 'Acesso ao Painel' : 'Criar Conta'}
              </h1>
            </div>
            <div className="p-10">
              <form onSubmit={handleAuth} className="space-y-6">
                {authError && <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold">{authError}</div>}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Seu E-mail</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold transition-all" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sua Senha</label>
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold transition-all" />
                </div>
                <button type="submit" disabled={isAuthLoading} className="w-full bg-indigo-600 text-white font-black py-5 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all">
                  {isAuthLoading ? <Loader2 className="animate-spin mx-auto" /> : (authMode === 'signin' ? 'Entrar' : 'Cadastrar')}
                </button>
                <button type="button" onClick={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')} className="w-full text-sm font-bold text-gray-400 hover:text-indigo-600">
                  {authMode === 'signin' ? 'Não tem conta? Cadastre-se' : 'Já tem conta? Faça login'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 font-sans">
      {successMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[120] bg-green-600 text-white px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top">
          <CheckCircle2 size={24} /><span className="font-bold">{successMsg}</span>
        </div>
      )}

      {selectedStore ? (
        <div className="space-y-8 animate-in slide-in-from-bottom">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1">
              <button onClick={() => setSelectedStore(null)} className="flex items-center gap-2 text-indigo-600 font-bold text-sm mb-2 hover:translate-x-[-4px] transition-transform">
                <ArrowLeft size={16} /> Painel Central
              </button>
              <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <StoreIcon className="text-indigo-600" size={32} />
                {selectedStore.name}
              </h1>
            </div>
            <div className="flex bg-gray-100 p-1.5 rounded-2xl self-start">
              <button onClick={() => setActiveTab('products')} className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${activeTab === 'products' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-400'}`}><Package size={18} /> Estoque</button>
              <button onClick={() => setActiveTab('coupons')} className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${activeTab === 'coupons' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-400'}`}><Tag size={18} /> Cupons</button>
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
             <h2 className="text-xl font-black text-gray-800 uppercase tracking-tighter">{activeTab === 'products' ? 'Controle de Estoque' : 'Cupons de Desconto'}</h2>
             <button onClick={() => activeTab === 'products' ? setIsProductModalOpen(true) : setIsCouponModalOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 px-8 rounded-2xl flex items-center gap-2 shadow-xl active:scale-95 transition-all"><Plus size={24} /> Novo {activeTab === 'products' ? 'Produto' : 'Cupom'}</button>
          </div>

          {isDataLoading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>
          ) : activeTab === 'products' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(product => (
                <div key={product.id} className="bg-white rounded-[2rem] border-2 border-gray-50 p-6 flex gap-4">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-sm flex-shrink-0">
                    <img src={product.image_url || 'https://via.placeholder.com/150'} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between">
                      <h4 className="font-black truncate">{product.name}</h4>
                      <div className="flex">
                        <button onClick={() => { setEditingProduct(product); setProductFormData({ ...product, description: product.description || '', image_url: product.image_url || '' }); setProductPreview(product.image_url || null); setIsProductModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-indigo-600"><Edit size={16}/></button>
                        <button onClick={() => productService.deleteProduct(product.id).then(() => loadProducts(selectedStore.id))} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={16}/></button>
                      </div>
                    </div>
                    <p className="text-lg font-black text-gray-900 mt-1">R$ {Number(product.price).toFixed(2)} <span className="text-[10px] text-gray-400 font-bold uppercase">/ {product.unit}</span></p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coupons.length === 0 && <div className="col-span-full py-20 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">Nenhum cupom ativo</div>}
              {coupons.map(c => (
                <div key={c.id} className="bg-white p-6 rounded-3xl border border-gray-100 flex justify-between items-center group">
                  <div className="flex items-center gap-3">
                    <div className="bg-indigo-50 p-3 rounded-2xl text-indigo-600"><Tag size={20} /></div>
                    <div>
                      <h4 className="font-black text-gray-900">{c.code}</h4>
                      <p className="text-[10px] font-bold text-gray-400">{c.type === 'percent' ? `${c.value}% de desconto` : `R$ ${c.value} de desconto`}</p>
                    </div>
                  </div>
                  <button onClick={() => couponService.deleteCoupon(c.id).then(() => loadCoupons(selectedStore.id))} className="p-3 text-red-200 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <h1 className="text-4xl font-black text-gray-900">Suas Lojas</h1>
              <p className="text-gray-400 font-bold text-sm uppercase tracking-widest">{user.email}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={signOut} className="bg-white border-2 border-gray-100 text-gray-500 font-bold py-4 px-6 rounded-2xl hover:bg-gray-50 transition-all"><LogOut size={20}/></button>
              <button onClick={() => openStoreModal()} className="bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 px-8 rounded-2xl flex items-center gap-2 shadow-xl transition-all"><Plus size={24} /> Criar Loja</button>
            </div>
          </div>
          {isStoresLoading ? (
             <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {stores.map(store => (
                <div key={store.id} className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm border border-gray-100 relative group">
                  <div className="absolute top-4 right-4 z-[40] flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                     <button onClick={() => handleShareStore(store.slug)} title="Compartilhar Link" className="p-3 bg-white rounded-xl text-green-600 shadow-xl border border-green-50 hover:bg-green-600 hover:text-white transition-all"><Share2 size={18} /></button>
                     <button onClick={() => openStoreModal(store)} className="p-3 bg-white rounded-xl text-indigo-600 shadow-xl border border-indigo-50 hover:bg-indigo-600 hover:text-white transition-all"><Edit size={18} /></button>
                     <button onClick={() => setStoreToDelete(store)} className="p-3 bg-white rounded-xl text-red-500 shadow-xl border border-red-50 hover:bg-red-600 hover:text-white transition-all"><Trash2 size={18} /></button>
                  </div>
                  <div className="h-32 bg-gray-100 relative">
                    <img src={store.banner_url} className="w-full h-full object-cover opacity-60" />
                    <div className="absolute -bottom-8 left-8"><img src={store.logo_url} className="w-20 h-20 rounded-2xl border-4 border-white bg-white shadow-xl object-cover" /></div>
                  </div>
                  <div className="p-8 pt-12">
                    <h3 className="text-xl font-black text-gray-900 truncate">{store.name}</h3>
                    <div className="grid grid-cols-2 gap-3 mt-6">
                      <button onClick={() => setSelectedStore(store)} className="bg-gray-900 text-white font-bold py-4 rounded-2xl text-xs hover:bg-black transition-all">Gerenciar</button>
                      <button onClick={() => window.location.hash = `#/${store.slug}`} className="bg-indigo-50 text-indigo-600 font-bold py-4 rounded-2xl text-xs flex items-center justify-center gap-2 hover:bg-indigo-100 transition-all"><ExternalLink size={14} /> Ver Loja</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Cupom */}
      {isCouponModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in">
            <div className="p-8 flex items-center justify-between border-b border-gray-50">
              <h2 className="text-2xl font-black flex items-center gap-2"><Ticket className="text-indigo-600" /> Novo Cupom</h2>
              <button onClick={() => setIsCouponModalOpen(false)} className="p-3 rounded-full hover:bg-gray-100 transition-all"><X size={24} /></button>
            </div>
            <form onSubmit={handleCouponSubmit} className="p-8 space-y-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Código do Cupom</label>
                <input type="text" required value={couponFormData.code} onChange={(e) => setCouponFormData(p => ({...p, code: e.target.value.toUpperCase()}))} className="w-full px-6 py-4 rounded-2xl bg-indigo-50 border-2 border-transparent focus:border-indigo-600 outline-none font-black text-indigo-600 placeholder:text-indigo-200" placeholder="EX: VEM10" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Tipo</label>
                  <select value={couponFormData.type} onChange={(e) => setCouponFormData(p => ({...p, type: e.target.value as 'percent' | 'fixed'}))} className="w-full px-6 py-4 rounded-2xl bg-gray-50 outline-none font-bold">
                    <option value="percent">Porcentagem (%)</option>
                    <option value="fixed">Valor Fixo (R$)</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor</label>
                  <input type="number" required value={couponFormData.value} onChange={(e) => setCouponFormData(p => ({...p, value: Number(e.target.value)}))} className="w-full px-6 py-4 rounded-2xl bg-gray-50 outline-none font-bold" />
                </div>
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-indigo-600 text-white font-black py-5 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                {isSubmitting ? <Loader2 className="animate-spin" /> : <Save size={20} />}
                Criar Cupom de Desconto
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal Loja */}
      {isStoreModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in">
            <div className="p-8 flex items-center justify-between border-b border-gray-50">
              <h2 className="text-2xl font-black">{editingStore ? 'Editar Loja' : 'Nova Loja'}</h2>
              <button onClick={() => setIsStoreModalOpen(false)} className="p-3 rounded-full hover:bg-gray-100 transition-all"><X size={24} /></button>
            </div>
            <form onSubmit={handleStoreSubmit} className="p-8 space-y-6 max-h-[80vh] overflow-y-auto">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Banner da Loja</label>
                <div className="relative h-32 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
                  {bannerPreview ? <img src={bannerPreview} className="w-full h-full object-cover" /> : <ImageIcon className="text-gray-300" />}
                  <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'banner')} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
              </div>
              <div className="flex gap-6 items-start">
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center shrink-0">
                   {logoPreview ? <img src={logoPreview} className="w-full h-full object-cover" /> : <ImageIcon className="text-gray-300" />}
                   <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'logo')} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nome Comercial</label>
                    <input type="text" required value={storeFormData.name} onChange={handleStoreNameChange} className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Endereço (Slug)</label>
                    <input type="text" required value={storeFormData.slug} onChange={(e) => setStoreFormData(p => ({...p, slug: generateSlug(e.target.value)}))} className="w-full px-6 py-4 rounded-2xl bg-indigo-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold text-indigo-600" />
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">WhatsApp</label>
                <input type="tel" required value={storeFormData.whatsapp_number} onChange={(e) => setStoreFormData(p => ({...p, whatsapp_number: e.target.value}))} className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold" placeholder="(00) 00000-0000" />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-indigo-600 text-white font-black py-5 rounded-2xl shadow-xl disabled:opacity-50">
                {isSubmitting ? <Loader2 className="animate-spin mx-auto" /> : 'Salvar Loja'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL PRODUTO COM UNIDADES EXPANDIDAS */}
      {isProductModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in">
            <div className="p-8 flex items-center justify-between border-b border-gray-50">
              <h2 className="text-2xl font-black">Produto</h2>
              <button onClick={() => { setIsProductModalOpen(false); setEditingProduct(null); }} className="p-3 rounded-full hover:bg-gray-100 transition-all"><X size={24} /></button>
            </div>
            <form onSubmit={handleProductSubmit} className="p-8 space-y-6 max-h-[80vh] overflow-y-auto">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="relative w-40 h-40 rounded-[2rem] overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center shrink-0">
                   {productPreview ? <img src={productPreview} className="w-full h-full object-cover" /> : <PackagePlus className="text-gray-300" size={32} />}
                   <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'product')} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                <div className="flex-1 space-y-4">
                  <input type="text" required value={productFormData.name} onChange={(e) => setProductFormData(p => ({...p, name: e.target.value}))} placeholder="Nome do Produto" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold" />
                  <div className="flex gap-4">
                    <input type="number" step="0.01" required value={productFormData.price} onChange={(e) => setProductFormData(p => ({...p, price: Number(e.target.value)}))} placeholder="R$ 0.00" className="flex-1 px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold" />
                    
                    <div className="flex-1 space-y-1">
                      <select 
                        value={productFormData.unit} 
                        onChange={(e) => setProductFormData(p => ({...p, unit: e.target.value}))} 
                        className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold"
                      >
                        <optgroup label="Unidades">
                          <option value="un">un (Unidade)</option>
                          <option value="dz">dz (Dúzia)</option>
                          <option value="par">par (Par)</option>
                          <option value="conj">conj (Conjunto)</option>
                        </optgroup>
                        <optgroup label="Peso">
                          <option value="kg">kg (Quilo)</option>
                          <option value="g">g (Grama)</option>
                          <option value="mg">mg (Miligrama)</option>
                        </optgroup>
                        <optgroup label="Volume">
                          <option value="l">l (Litro)</option>
                          <option value="ml">ml (Mililitro)</option>
                        </optgroup>
                        <optgroup label="Comprimento/Área">
                          <option value="m">m (Metro)</option>
                          <option value="cm">cm (Centímetro)</option>
                          <option value="mm">mm (Milímetro)</option>
                          <option value="m2">m² (Metro Quadrado)</option>
                        </optgroup>
                        <optgroup label="Embalagens">
                          <option value="pct">pct (Pacote)</option>
                          <option value="cx">cx (Caixa)</option>
                          <option value="frd">frd (Fardo)</option>
                          <option value="bdj">bdj (Bandeja)</option>
                          <option value="pot">pot (Pote)</option>
                          <option value="lt">lt (Lata)</option>
                          <option value="gf">gf (Garrafa)</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <input type="text" required value={productFormData.category} onChange={(e) => setProductFormData(p => ({...p, category: e.target.value}))} placeholder="Categoria (ex: Bebidas)" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold" />
                <textarea value={productFormData.description} onChange={(e) => setProductFormData(p => ({...p, description: e.target.value}))} placeholder="Descrição curta" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-indigo-600 outline-none font-bold h-24 resize-none" />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-indigo-600 text-white font-black py-5 rounded-2xl shadow-xl disabled:opacity-50">Confirmar</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
